import { App, Collapse, DatePicker, Form, Input, type UploadFile } from 'antd';
import dayjs from 'dayjs';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'translations/hooks';

import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  HeaderType,
  ReportHeader,
} from 'business/quality-control/pages/defect-report-history/components/defect-report-details-drawer/components/report-header';
import { DefectReportStatus } from 'business/quality-control/pages/defect-report-history/hooks/data/types';
import { db, RepairReport } from 'technical/db';
import { repairReportPictureSyncService } from 'technical/db/sync/repair-report-picture-sync.service';
import {
  picturesBase64ToFiles,
  PictureUploadInput,
} from 'technical/picture-upload-input';

import { RepairFormButtons } from './buttons';
import { RepairFormValues } from './types';

export const RepairForm = ({
  defectReportId,
  repairReportId,
  repairReport,
  closeDefect,
  isRingCompliant,
}: {
  defectReportId: string;
  repairReportId: string;
  repairReport: RepairReport | null;
  closeDefect: () => void;
  isRingCompliant: boolean;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RepairFormValues>();
  const { message } = App.useApp();
  const { extraQualityPermissions } = usePermissions();

  const formActionRef = useRef<'save' | 'submit'>('save');

  const isSubmitted = repairReport?.submittedAt != null || isRingCompliant;
  const isFormInputEditable =
    !isSubmitted && extraQualityPermissions.canEditRepair;

  const rawPictures = useLiveQuery(
    () =>
      db.repairReportPictures
        .where('repairReportId')
        .equals(repairReportId)
        .toArray(),
    [],
  );

  useEffect(() => {
    if (rawPictures !== undefined) {
      form.setFieldsValue({ pictures: picturesBase64ToFiles(rawPictures) });
    }
  }, [rawPictures, form]);

  let repairStatus = DefectReportStatus.RepairInProgress;
  if (repairReport && repairReport.submittedAt != null) {
    repairStatus = DefectReportStatus.RepairDone;
  }

  const handleInvalidate = async () => {
    try {
      await db.repairReports.update(repairReportId, {
        submittedAt: null,
        desynchronizedAt: dayjs().toISOString(),
      });
      message.open({
        type: 'success',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.repairform.invalidateSuccess',
        ),
      });
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.repairform.invalidateError',
        ),
      });
    }
  };

  const handleRemovePicture = async (file: UploadFile): Promise<boolean> => {
    try {
      await repairReportPictureSyncService.markForDeletion(file.uid);
      return true;
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.repairform.pictureDeleteError',
        ),
      });
      return false;
    }
  };

  const save = async (row: RepairFormValues) => {
    try {
      const submittedAt =
        formActionRef.current === 'submit' ? dayjs().toISOString() : null;

      await db.repairReports.put({
        id: repairReportId,
        defectReportId,
        author: repairReport?.author ?? 'Current user',
        description: row.description,
        createdAt: row.date.toISOString(),
        submittedAt: submittedAt,
        desynchronizedAt: dayjs().toISOString(),
        deletedAt: null,
      });

      if (row.pictures.length > 0) {
        await repairReportPictureSyncService.createBulk(
          repairReportId,
          row.pictures,
        );
      }

      message.open({
        type: 'success',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.repairform.success',
        ),
      });
      closeDefect();
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.repairform.error',
        ),
      });
    }
  };

  const initialValues: RepairFormValues = {
    date: repairReport ? dayjs(repairReport.createdAt).tz() : dayjs().tz(),
    description: repairReport?.description ?? '',
    pictures: [],
  };

  return (
    <Collapse
      defaultActiveKey={['1']}
      expandIconPosition="end"
      style={{ background: '#fff' }}
      items={[
        {
          key: '1',
          label: (
            <ReportHeader status={repairStatus} type={HeaderType.repair} />
          ),
          children: (
            <>
              <Form<RepairFormValues>
                form={form}
                layout="vertical"
                onFinish={save}
                initialValues={initialValues}
              >
                <Form.Item<RepairFormValues>
                  name="date"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.repairform.date',
                  )}
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    showTime={{ format: 'HH:mm' }}
                    disabled={!isFormInputEditable}
                    format="L HH:mm"
                    inputReadOnly
                  />
                </Form.Item>

                <Form.Item<RepairFormValues>
                  name="description"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.repairform.description',
                  )}
                >
                  <Input disabled={!isFormInputEditable} />
                </Form.Item>

                <Form.Item<RepairFormValues>
                  name="pictures"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.pictures',
                  )}
                >
                  <PictureUploadInput
                    maxCount={3}
                    disabled={!isFormInputEditable}
                    showRemoveIcon={isFormInputEditable}
                    onRemove={handleRemovePicture}
                  />
                </Form.Item>
              </Form>

              <RepairFormButtons
                form={form}
                formActionRef={formActionRef}
                isRingCompliant={isRingCompliant}
                isValidated={isSubmitted}
                canEditRepair={extraQualityPermissions.canEditRepair}
                canInvalidateRepair={
                  extraQualityPermissions.canInvalidateRepair
                }
                onInvalidate={handleInvalidate}
              />
            </>
          ),
        },
      ]}
    />
  );
};
